import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/auth.ts';

export const api: AxiosInstance = axios.create({ baseURL: '/api', withCredentials: true });

api.interceptors.request.use((cfg) => {
  const t = useAuthStore.getState().accessToken;
  if (t && cfg.headers) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Outcome of trying to recover an expired session on a 401:
//  - 'recovered': got a fresh access token; retry the original request.
//  - 'dead':      session is genuinely gone (revoked/deactivated) → show login.
//  - 'transient': a network/5xx blip prevented recovery → DON'T log out; the
//                 caller can retry. Sessions only end on a real auth failure or
//                 an explicit manual logout, never on a transient hiccup.
type Recovery = 'recovered' | 'dead' | 'transient';

async function recoverSession(): Promise<Recovery> {
  try {
    await useAuthStore.getState().refresh();
    return 'recovered';
  } catch (e) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 401) {
      // Refresh token is genuinely revoked/expired. Try the ambient wap_sso
      // cookie before giving up — a sibling app may still hold a live session.
      const ok = await useAuthStore.getState().ssoBootstrap();
      return ok ? 'recovered' : 'dead';
    }
    // Network error / 5xx — leave the session intact so a blip can't log us out.
    return 'transient';
  }
}

// Single in-flight recovery shared across all 401s so concurrent requests don't
// each fire a refresh (which would race the rotation and defeat the point).
let refreshing: Promise<Recovery> | null = null;
api.interceptors.response.use(
  (r) => r,
  async (err: {
    response?: { status?: number };
    config?: InternalAxiosRequestConfig & { _retry?: boolean };
  }) => {
    const status = err.response?.status;
    const original = err.config;
    if (status === 401 && original && !original._retry) {
      original._retry = true;
      refreshing ??= recoverSession().finally(() => {
        refreshing = null;
      });
      const outcome = await refreshing;
      // The request interceptor re-reads the (now fresh) access token on retry.
      if (outcome === 'recovered') return api(original);
      if (outcome === 'dead') {
        useAuthStore.setState({ accessToken: null, refreshToken: null, user: null });
        if (typeof window !== 'undefined') window.location.href = '/login';
      }
      // 'transient' → fall through and reject; the session stays signed in.
    }
    throw err;
  },
);
